import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getMessageQueryKey,
  messageBelongsToConversation,
  readerBelongsToConversation,
} from "../components/Messages/utils/messageConversation.utils";

export const useMessageSocket = (socket, receiver, onNewMessage, { workforces, self, companyId } = {}) => {
  const queryClient = useQueryClient();
  const processedMessageUidsRef = useRef(new Set());
  const queryKey = useMemo(
    () => getMessageQueryKey(receiver, companyId),
    [receiver?.type, receiver?.id, receiver?.uid, companyId],
  );

  useEffect(() => {
    if (!socket || !receiver) return;

    const handleNewMessage = (messageRecord) => {
      if (!messageBelongsToConversation(messageRecord, receiver, { workforces, self, companyId })) return;

      const messageUid = messageRecord.uid;
      if (!messageUid || processedMessageUidsRef.current.has(messageUid)) return;
      processedMessageUidsRef.current.add(messageUid);

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData || !Array.isArray(oldData.pages)) {
          return { pages: [{ items: [messageRecord] }], pageParams: [1] };
        }

        const pages = [...oldData.pages];
        const items = pages[0].items || [];
        if (items.some((m) => m.uid === messageRecord.uid)) return oldData;

        const updatedPage = { ...pages[0], items: [messageRecord, ...items] };
        onNewMessage?.();
        return { ...oldData, pages: [updatedPage, ...pages.slice(1)] };
      });
    };

    socket.on("messageRecord", handleNewMessage);

    return () => {
      socket.off("messageRecord", handleNewMessage);
    };
  }, [
    socket,
    queryClient,
    onNewMessage,
    receiver?.type,
    receiver?.id,
    receiver?.uid,
    workforces,
    self?.type,
    self?.id,
    self?.uid,
    companyId,
    queryKey,
  ]);
};

/**
 * Listens for "messageRead" (emitted when the peer marks the open conversation as read)
 * and patches readAt on our own cached messages so their status flips to "Read" live.
 */
export const useMessageReadSocket = (socket, receiver, sender, { workforces, companyId } = {}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => getMessageQueryKey(receiver, companyId),
    [receiver?.type, receiver?.id, receiver?.uid, companyId],
  );

  useEffect(() => {
    if (!socket || !receiver || !sender) return;

    const isMine = (msgSender) =>
      msgSender?.type === sender?.type &&
      (sender?.uid && msgSender?.uid
        ? String(sender.uid) === String(msgSender.uid)
        : Number(sender?.id) === Number(msgSender?.id));

    const handleMessageRead = ({ reader, readAt, companyId: readCompanyId } = {}) => {
      if (!readerBelongsToConversation(reader, receiver, { workforces, companyId, readCompanyId })) return;

      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData || !Array.isArray(oldData.pages)) return oldData;

        const pages = oldData.pages.map((page) => ({
          ...page,
          items: (page.items || []).map((item) => (!item.readAt && isMine(item.sender) ? { ...item, readAt } : item)),
        }));

        return { ...oldData, pages };
      });
    };

    socket.on("messageRead", handleMessageRead);

    return () => {
      socket.off("messageRead", handleMessageRead);
    };
  }, [socket, queryClient, receiver?.type, receiver?.id, receiver?.uid, sender?.type, sender?.id, sender?.uid, workforces, companyId, queryKey]);
};
